"use client";
import { InfoCircleOutlined } from "@ant-design/icons";
import CalendarIcon from "@untitled-ui/icons-react/build/esm/Calendar";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import {
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Table,
  Tooltip,
} from "antd";
import { useEffect, useMemo, useState } from "react";

const formatNaira = (num) =>
  "₦" + Number(num || 0).toLocaleString("en-NG");

const productOptions = [
  { value: "consumer_term_monthly", label: "Consumer Term Loan -- Monthly" },
  {
    value: "consumer_term_quarterly",
    label: "Consumer Term Loan -- Quarterly",
  },
  { value: "sme_working_capital", label: "SME Working Capital" },
  { value: "mortgage_residential", label: "Mortgage -- Residential" },
];

const purposeOptions = [
  { value: "working_capital", label: "Business capital -- working capital" },
  { value: "asset_acquisition", label: "Asset acquisition" },
  { value: "education", label: "Education" },
  { value: "medical", label: "Medical expenses" },
  { value: "personal", label: "Personal / consumption" },
];

const sectionHeaderStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#475467",
  letterSpacing: "0.05em",
  marginBottom: 16,
};

const sectionDividerStyle = {
  borderTop: "1px dashed #e5e7eb",
  paddingTop: 24,
  marginTop: 24,
};

const LoanParameters = (props) => {
  const { onFinish, form, initialValues, customerData } = props;

  const loanAmount = Form.useWatch("loanAmount", form);
  const tenor = Form.useWatch("tenor", form);
  const interestRate = Form.useWatch("interestRate", form);

  const [schedule, setSchedule] = useState([]);
  const [emi, setEmi] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalRepayment, setTotalRepayment] = useState(0);

  const netIncome = customerData?.monthlyNetIncome || 0;

  useEffect(() => {
    const P = parseFloat(loanAmount) || 0;
    const n = parseInt(tenor, 10) || 0;
    const annualRate = parseFloat(interestRate) || 0;

    if (P <= 0 || n <= 0 || annualRate <= 0) {
      setEmi(0);
      setTotalInterest(0);
      setTotalRepayment(0);
      setSchedule([]);
      return;
    }

    const r = annualRate / 12 / 100;
    const rPowN = Math.pow(1 + r, n);
    const monthlyEmi = (P * r * rPowN) / (rPowN - 1);

    setEmi(monthlyEmi);
    setTotalRepayment(monthlyEmi * n);
    setTotalInterest(monthlyEmi * n - P);

    // Build amortization schedule
    const rows = [];
    let balance = P;
    const today = new Date();

    for (let i = 1; i <= n; i++) {
      const interestPortion = balance * r;
      const principalPortion = monthlyEmi - interestPortion;
      const closingBalance = balance - principalPortion;
      const dueDate = new Date(today);
      dueDate.setMonth(dueDate.getMonth() + i);

      rows.push({
        key: i,
        month: i,
        dueDate: dueDate.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        openingBalance: balance,
        principal: principalPortion,
        interest: interestPortion,
        emi: monthlyEmi,
        closingBalance: Math.max(closingBalance, 0),
      });

      balance = closingBalance;
    }

    setSchedule(rows);
  }, [loanAmount, tenor, interestRate]);

  const dsrRatio = useMemo(() => {
    if (!netIncome || netIncome <= 0 || emi <= 0) return 0;
    return (emi / netIncome) * 100;
  }, [emi, netIncome]);

  const processingFee = useMemo(() => {
    const amount = parseFloat(loanAmount) || 0;
    return amount * 0.01;
  }, [loanAmount]);

  const insurancePremium = useMemo(() => {
    const amount = parseFloat(loanAmount) || 0;
    return amount * 0.005;
  }, [loanAmount]);

  useEffect(() => {
    form.setFieldsValue({
      processingFee: processingFee ? formatNaira(processingFee) : "",
      insurancePremium: insurancePremium ? formatNaira(insurancePremium) : "",
    });
  }, [processingFee, insurancePremium, form]);

  const scheduleColumns = [
    {
      title: "#",
      dataIndex: "month",
      key: "month",
      width: 50,
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      width: 120,
    },
    {
      title: "Opening Bal.",
      dataIndex: "openingBalance",
      key: "openingBalance",
      render: (val) => formatNaira(val?.toFixed(2)),
    },
    {
      title: "Principal",
      dataIndex: "principal",
      key: "principal",
      render: (val) => (
        <span style={{ color: "#0d9488", fontWeight: 500 }}>
          {formatNaira(val?.toFixed(2))}
        </span>
      ),
    },
    {
      title: "Interest",
      dataIndex: "interest",
      key: "interest",
      render: (val) => (
        <span style={{ color: "#d97706", fontWeight: 500 }}>
          {formatNaira(val?.toFixed(2))}
        </span>
      ),
    },
    {
      title: "EMI",
      dataIndex: "emi",
      key: "emi",
      render: (val) => formatNaira(val?.toFixed(2)),
    },
    {
      title: "Closing Bal.",
      dataIndex: "closingBalance",
      key: "closingBalance",
      render: (val) => formatNaira(val?.toFixed(2)),
    },
  ];

  return (
    <div>
      <Form
        className="custom-form"
        layout="vertical"
        onFinish={onFinish}
        form={form}
        initialValues={initialValues}
        style={{ width: "100%" }}
      >
        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Loan Product
                  <Tooltip title="Select the loan product type">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="loanProduct"
              rules={[
                {
                  required: true,
                  message: "Loan product is required",
                },
              ]}
            >
              <Select
                placeholder="Select loan product"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                options={productOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Loan Purpose
                  <Tooltip title="Select the purpose of the loan">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="loanPurpose"
              rules={[
                {
                  required: true,
                  message: "Loan purpose is required",
                },
              ]}
            >
              <Select
                placeholder="Select loan purpose"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                options={purposeOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Loan Amount
                  <Tooltip title="Enter the desired loan amount">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="loanAmount"
              rules={[
                {
                  required: true,
                  message: "Loan amount is required",
                },
              ]}
              help="Min ₦500,000 · Max ₦50,000,000"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                addonBefore={
                  <span style={{ fontWeight: 500, color: "#344054" }}>
                    {"₦"}
                  </span>
                }
                placeholder="0.00"
                type="number"
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Tenor in Months
                  <Tooltip title="Enter the loan repayment tenor in months">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="tenor"
              rules={[
                {
                  required: true,
                  message: "Tenor is required",
                },
              ]}
              help="3 -- 60 months"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="e.g. 24"
                type="number"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Interest Rate % p.a.
                  <Tooltip title="Annual interest rate (declining balance)">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="interestRate"
              help="Product rate · declining balance"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="e.g. 18"
                readOnly
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Disbursement Date
                  <Tooltip title="Select the loan disbursement date">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="disbursementDate"
              rules={[
                {
                  required: true,
                  message: "Disbursement date is required",
                },
              ]}
            >
              <DatePicker
                className="bond-date-picker"
                style={{
                  width: "100%",
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 11px",
                  fontSize: 14,
                }}
                suffixIcon={<CalendarIcon color="#667085" width={20} />}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  First Repayment Date
                  <Tooltip title="Select the first repayment date">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="firstRepaymentDate"
              rules={[
                {
                  required: true,
                  message: "First repayment date is required",
                },
              ]}
            >
              <DatePicker
                className="bond-date-picker"
                style={{
                  width: "100%",
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 11px",
                  fontSize: 14,
                }}
                suffixIcon={<CalendarIcon color="#667085" width={20} />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Processing Fee
                  <Tooltip title="Automatically computed processing fee">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="processingFee"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="Computed automatically"
                readOnly
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Insurance Premium
                  <Tooltip title="Automatically computed insurance premium">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="insurancePremium"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="Computed automatically"
                readOnly
              />
            </Form.Item>
          </Col>
        </Row>

        {/* LIVE LOAN CALCULATOR */}
        <div style={{ marginTop: 24, marginBottom: 24 }}>
          <div
            style={{
              background: "#f0fdf9",
              borderRadius: 12,
              padding: 24,
              border: "1px solid #a7f3d0",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#344054",
                letterSpacing: "0.1em",
                marginBottom: 20,
                textTransform: "uppercase",
              }}
            >
              LIVE LOAN CALCULATOR
            </div>
            <Row>
              <Col span={6} style={{ textAlign: "center", padding: "0px 8px" }}>
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: "#15CCAE",
                    lineHeight: 1.2,
                  }}
                >
                  {emi > 0 ? formatNaira(emi.toFixed(0)) : "--"}
                </div>
                <div
                  style={{
                    color: "#667085",
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  Monthly EMI · per month
                </div>
              </Col>
              <Col span={6} style={{ textAlign: "center", padding: "0px 8px" }}>
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: "#344054",
                    lineHeight: 1.2,
                  }}
                >
                  {totalInterest > 0
                    ? formatNaira(totalInterest.toFixed(0))
                    : "--"}
                </div>
                <div
                  style={{
                    color: "#667085",
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  Total Interest · over {tenor || 0} months
                </div>
              </Col>
              <Col span={6} style={{ textAlign: "center", padding: "0px 8px" }}>
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: "#15CCAE",
                    lineHeight: 1.2,
                  }}
                >
                  {totalRepayment > 0
                    ? formatNaira(totalRepayment.toFixed(0))
                    : "--"}
                </div>
                <div
                  style={{
                    color: "#667085",
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  Total Repayment · principal + interest
                </div>
              </Col>
              <Col span={6} style={{ textAlign: "center", padding: "0px 8px" }}>
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: "#344054",
                    lineHeight: 1.2,
                  }}
                >
                  {dsrRatio > 0 ? dsrRatio.toFixed(1) + "%" : "--"}
                </div>
                <div
                  style={{
                    color: "#667085",
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  DSR ratio · of net income
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* REPAYMENT SCHEDULE PREVIEW */}
        <div style={sectionDividerStyle}>
          <div style={sectionHeaderStyle}>REPAYMENT SCHEDULE PREVIEW</div>
        </div>

        {schedule.length > 0 ? (
          <div style={{ marginBottom: 24 }}>
            <Table
              columns={scheduleColumns}
              dataSource={schedule.slice(0, 6)}
              bordered
              pagination={false}
              size="small"
            />
            <div
              style={{
                color: "#667085",
                fontSize: 13,
                marginTop: 8,
                textAlign: "center",
              }}
            >
              Showing 6 of {tenor || 0} instalments · View full schedule
            </div>
          </div>
        ) : (
          <div
            style={{
              color: "#667085",
              fontSize: 13,
              textAlign: "center",
              padding: "24px 0",
              marginBottom: 24,
            }}
          >
            Enter loan amount, tenor, and interest rate to preview the
            repayment schedule
          </div>
        )}
      </Form>
    </div>
  );
};

export default LoanParameters;
